import React, { useState } from 'react'
import { useProviders, useAmbulanceProviders, useSearchProviders } from '../../hooks/useProviders'
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  StarIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import { ServiceType } from '../../types'
import LoadingSpinner from '../../components/LoadingSpinner'

function ProvidersPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedService, setSelectedService] = useState<ServiceType | undefined>()
  const [selectedCity, setSelectedCity] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showAmbulanceOnly, setShowAmbulanceOnly] = useState(false)

  const { data: providersData, isLoading: providersLoading } = useProviders({
    serviceType: showAmbulanceOnly ? ServiceType.AMBULANCE_TRANSPORT : selectedService,
    city: selectedCity || undefined,
    page: currentPage,
    limit: 12,
  })

  
  const { data: ambulanceProviders, isLoading: ambulanceLoading } = useAmbulanceProviders()

  const searchMutation = useSearchProviders()
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const serviceTypes: { value: ServiceType; label: string }[] = [
    { value: ServiceType.AMBULANCE_TRANSPORT, label: 'Ambulance Transport' },
    { value: ServiceType.GHUSL_SERVICE, label: 'Ghusl (Ritual Washing)' },
    { value: ServiceType.KAFAN_SERVICE, label: 'Kafan (Shrouding)' },
    { value: ServiceType.JANAZAH_LEAD, label: 'Janazah Prayer Lead' },
    { value: ServiceType.BURIAL_PLOT, label: 'Burial Plot' },
    { value: ServiceType.CATERING, label: 'Catering Services' },
    { value: ServiceType.FLOWERS, label: 'Flowers & Decorations' },
    { value: ServiceType.TENT_RENTAL, label: 'Tent & Equipment Rental' },
  ]

  const majorCities = [
    'Kuala Lumpur',
    'Petaling Jaya',
    'Shah Alam',
    'Johor Bahru',
    'Penang',
    'Ipoh',
    'Malacca',
    'Kota Kinabalu',
    'Kuching',
  ]

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!searchQuery.trim()) return

    setIsSearching(true)
    try {
      const results = await searchMutation.mutateAsync({
        query: searchQuery,
        serviceType: selectedService,
      })
      setSearchResults(results)
    } catch (error) {
      // Error is handled by the mutation hook
    } finally {
      setIsSearching(false)
    }
  }

  const renderProviderCard = (provider: any) => (
    <div key={provider.id} className="card hover:shadow-lg transition-shadow">
      <div className="card-body">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-neutral-900">
                {provider.companyName || `${provider.users.firstName} ${provider.users.lastName}`}
              </h3>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-800`}>
                {provider.serviceType.replace('_', ' ')}
              </span>
            </div>

            {provider.description && (
              <p className="mt-2 text-sm text-neutral-600 line-clamp-2">
                {provider.description}
              </p>
            )}

            <div className="mt-4 space-y-2">
              <div className="flex items-center text-sm text-neutral-500">
                <MapPinIcon className="h-4 w-4 mr-2" />
                {provider.city ? `${provider.city}${provider.state ? ', ' + provider.state : ''}` : 'Location not available'}
              </div>

              <div className="flex items-center text-sm text-neutral-500">
                <PhoneIcon className="h-4 w-4 mr-2" />
                {provider.user?.phone || provider.phone || 'No phone available'}
              </div>

              <div className="flex items-center text-sm text-neutral-500">
                <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                {provider.address || 'Address not available'}
              </div>
            </div>
          </div>

          <div className="ml-4 text-right">
            <div className="flex items-center">
              <StarSolidIcon className="h-5 w-5 text-yellow-400" />
              <span className="ml-1 text-sm font-medium text-neutral-900">
                {provider.rating?.toFixed(1) || '0.0'}
              </span>
              <span className="ml-1 text-sm text-neutral-500">
                ({provider.reviewCount || 0})
              </span>
            </div>

            <div className="mt-2 text-xs text-neutral-500">
              {provider._count.bookings} bookings
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-neutral-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-green-600">
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              Verified Provider
            </div>

            <button className="btn-primary text-sm">
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  if (providersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-neutral-900">Service Providers</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Find verified funeral service providers across Malaysia
        </p>
      </div>

      {/* Emergency Ambulance Section */}
      <div className="mb-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <ClockIcon className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-medium text-red-900">Need Emergency Ambulance?</h3>
              <p className="mt-1 text-sm text-red-700">
                Get immediate transport services for deceased collection and transfer.
              </p>
            </div>
            <button
              onClick={() => setShowAmbulanceOnly(!showAmbulanceOnly)}
              className="btn bg-red-600 text-white hover:bg-red-700"
            >
              {showAmbulanceOnly ? 'Show All Services' : 'Find Ambulance'}
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-neutral-400" />
                </div>
                <input
                  type="text"
                  className="input pl-10"
                  placeholder="Search providers by name, service, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <button
              type="submit"
              className="btn-outline"
              disabled={isSearching}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>

          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Service Type
              </label>
              <select
                className="input"
                value={selectedService || ''}
                onChange={(e) => setSelectedService(e.target.value as ServiceType || undefined)}
              >
                <option value="">All Services</option>
                {serviceTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                City
              </label>
              <select
                className="input"
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
              >
                <option value="">All Cities</option>
                {majorCities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </form>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-medium text-neutral-900 mb-4">Search Results</h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {searchResults.map(renderProviderCard)}
          </div>
        </div>
      )}

      {/* Providers Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {(providersData?.data || []).map(renderProviderCard)}
      </div>

      {/* Empty State */}
      {(!providersData?.data || providersData.data.length === 0) && searchResults.length === 0 && (
        <div className="text-center py-12">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-neutral-400" />
          <h3 className="mt-2 text-sm font-medium text-neutral-900">No providers found</h3>
          <p className="mt-1 text-sm text-neutral-500">
            Try adjusting your search criteria or filters.
          </p>
        </div>
      )}

      {/* Pagination */}
      {providersData && providersData.totalPages > 1 && (
        <div className="mt-8 flex items-center justify-between">
          <div className="text-sm text-neutral-700">
            Showing {((currentPage - 1) * 12) + 1} to{' '}
            {Math.min(currentPage * 12, providersData.total)} of{' '}
            {providersData.total} providers
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-outline px-3 py-1 text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm text-neutral-700">
              Page {currentPage} of {providersData.totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(providersData.totalPages, currentPage + 1))}
              disabled={currentPage === providersData.totalPages}
              className="btn-outline px-3 py-1 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProvidersPage